import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Grid,
  Switch,
  CircularProgress
} from '@material-ui/core';
import { ConfirmDialog } from 'components';
import {
  addTasksToQueueApi,
  updateQuoteProductSubscriptionOptionApi,
  getAllTasksFromQueueApi,
  deleteQuoteProductSubscriptionOptionApi,
  getQuoteProAttributeDataApi,
  getStoreCustomerGroupsApi
} from 'api';
import useProducts from 'hooks/useProducts';
import { frequencyOptions } from './helpers';
import AddSubscriptionButton from './AddSubscriptionButton';
import AddSubscriptionModal from './AddSubscriptionModal';
import EditSubscriptionModal from './EditSubscriptionModal';
import SubscriptionOptionsTable from './SubscriptionOptionsTable';
import SubscriptionAttributeSelect from './SubscriptionAttributeSelect';
import SubscriptionAttributeInput from './SubscriptionAttributeInput';


const Subscription = () => {
  const dispatch = useDispatch();

  const [subscriptionAttributes, setSubscriptionAttributes] = useState(
    {
      isSubscriptionActive: false,
      isOneTimeEnable: false,
      subscriptionLabel: '',
      minimumTimePeriod: '365',
      rebillDate: '',
      customerIncludeToGroup: '',
      customerExcludeToGroup: ''
    }
  );
  const [openAddingSubscriptionsDialog, setOpenAddingSubscriptionsDialog] = useState(false);
  const [editableSubscription, setEditableSubscription] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const {
    isSubscriptionActive,
    isOneTimeEnable,
    subscriptionLabel,
    minimumTimePeriod,
    rebillDate,
    customerIncludeToGroup,
    customerExcludeToGroup
  } = subscriptionAttributes;

  const {
    editableQuoteProduct,
    editableQuoteProductWithoutChanges,
    quoteProductSubscriptionOptions,
    quoteProProductAttributeByCode,
    quoteSubscriptionOptionsLoading,
    loadingQuoteProductAttributes,
  } = useSelector((state: IReduxState) => state.quote);
  const {
    loadingTasksToQueue
  } = useSelector((state: IReduxState) => state.products);
  const {
    customerGroups
  } = useSelector((state: IReduxState) => state.customers);
  const {
    profile: { stores }
  } = useSelector((state: IReduxState) => state.user);
  const [{quoteProStoreCode}] = stores;

  const {
    queueTasks,
    queueTasksSkus
  } = useProducts(quoteProStoreCode);

  useEffect(() => {
    getAllTasksFromQueueApi({dispatch, storeViewCode: quoteProStoreCode});
  }, [queueTasks.length]);

  useEffect(() => {
    if (!quoteProProductAttributeByCode?.['rebill_date']) {
      getQuoteProAttributeDataApi({dispatch, attributeCode: 'rebill_date'});
    }

    if (!customerGroups.length) {
      getStoreCustomerGroupsApi({dispatch});
    }
  }, []);

  const existFrequency = quoteProductSubscriptionOptions.length ?
    quoteProductSubscriptionOptions.map((o) => `${o.frequency_count} ${o.frequency_unit}${o.frequency_count > 1 ? 's' : ''}`)
    : [];
  const isMembershipProduct = editableQuoteProduct?.sku.includes('MEMBER');

  useEffect(() => {
    const subscriptionAttributes = editableQuoteProduct?.custom_attributes?.reduce((acc, attr) => {
      if (attr.attribute_code === 'subscription_active') {
        return {...acc, isSubscriptionActive: attr.value == 1};
      }

      if (attr.attribute_code === 'subscription_allow_onetime') {
        return {...acc, isOneTimeEnable: attr.value == 1};
      }

      if (attr.attribute_code === 'subscription_label') {
        return {...acc, subscriptionLabel: attr.value};
      }

      if (attr.attribute_code === 'minimum_time_period') {
        return {...acc, minimumTimePeriod: attr.value};
      }

      if (attr.attribute_code === 'rebill_date') {
        return {...acc, rebillDate: attr.value};
      }

      if (attr.attribute_code === 'subscription_include_to_group') {
        return {...acc, customerIncludeToGroup: attr.value};
      }

      if (attr.attribute_code === 'subscription_exclude_to_group') {
        return {...acc, customerExcludeToGroup: attr.value};
      }

      return acc;
    }, {});
    setSubscriptionAttributes((prevState) => ({...prevState, ...subscriptionAttributes}));
  }, [editableQuoteProduct]);

  const isAllowToAddSubscription = (!isOneTimeEnable && frequencyOptions.length !== existFrequency.length) ||
                                   (isOneTimeEnable && frequencyOptions.length !== (existFrequency.length - 1));

  const isTaskInQueue = queueTasksSkus.find((sku) => sku === editableQuoteProduct?.sku);

  const isLoading = quoteSubscriptionOptionsLoading ||
                    isTaskInQueue ||
                    loadingTasksToQueue ||
                    loadingQuoteProductAttributes;

  const createOneTimePurchaseOption = () => {
    const option = [{
      product_id: editableQuoteProduct.id,
      frequency_count: 0,
      frequency_unit: null,
      length: null,
      installment_price: null,
      adjustment_price: null,
      sproduct_label: null,
      ssort_order: 0
    }];

    const productToUpdate = {
      sku: editableQuoteProduct.sku,
      custom_attributes: [{
        attribute_code: 'subscription_allow_onetime',
        value: '1'
      }]
    };

    const task = {
      body: productToUpdate,
      name: editableQuoteProduct.name,
      sku: editableQuoteProduct.sku,
      requestType: 'UPDATE_PRODUCT',
      storeViewCode: quoteProStoreCode
    };

    updateQuoteProductSubscriptionOptionApi({
      dispatch,
      subscriptions: option,
      isNewOptions: true
    });
    addTasksToQueueApi({dispatch, tasks: [task]});
  };

  const deleteOneTimePurchaseOption = async () => {
    const id = quoteProductSubscriptionOptions?.find((option) => option.frequency_count == 0)?.id;

    if (id) {
      await deleteQuoteProductSubscriptionOptionApi({dispatch, id});
    }

    const productToUpdate = {
      sku: editableQuoteProduct.sku,
      custom_attributes: [{
        attribute_code: 'subscription_allow_onetime',
        value: '0'
      }]
    };
    const task = {
      body: productToUpdate,
      name: editableQuoteProduct.name,
      sku: editableQuoteProduct.sku,
      requestType: 'UPDATE_PRODUCT',
      storeViewCode: quoteProStoreCode
    };
    addTasksToQueueApi({dispatch, tasks: [task]});
  };

  const handleSaveSubscriptionOption = async (values) => {
    setOpenAddingSubscriptionsDialog(false);
    setEditableSubscription(null);

    const isPriceChanged = editableQuoteProduct.price !== editableQuoteProductWithoutChanges.price;

    if (isPriceChanged) {
      const task = {
        body: editableQuoteProduct,
        name: editableQuoteProduct.name,
        sku: editableQuoteProduct.sku,
        requestType: 'UPDATE_PRODUCT',
        storeViewCode: quoteProStoreCode
      };

      await addTasksToQueueApi({dispatch, tasks: [task]});
    }

    await updateQuoteProductSubscriptionOptionApi({
      dispatch,
      subscriptions: values?.editedSubscriptionOption
        ? values.editedSubscriptionOption
        : values,
      isNewOptions: !values?.editedSubscriptionOption
    });

    if (!isSubscriptionActive) {
      const productToUpdate = {
        sku: editableQuoteProduct?.sku,
        custom_attributes: [
          {attribute_code: 'subscription_active', value: 1}
        ]
      };
      const task = {
        body: productToUpdate,
        name: editableQuoteProduct?.name,
        sku: editableQuoteProduct?.sku,
        requestType: 'UPDATE_PRODUCT',
        storeViewCode: quoteProStoreCode
      };
      addTasksToQueueApi({dispatch, tasks: [task]});
    }
  };

  const handleChangeSubscriptionStatus = (checked) => {
    const isSubscriptionEnable = editableQuoteProduct.custom_attributes?.find(
      (a) => a.attribute_code === 'subscription_active')?.value == 1;
    const isOneTimeEnabled = editableQuoteProduct.custom_attributes?.find(
      (a) => a.attribute_code === 'subscription_allow_onetime')?.value == 1;
    const currentGroupsValue = editableQuoteProduct?.custom_attributes
      .find((attr) => attr.attribute_code === 'catalog_quotepro_groups')?.value || '';

    const isNoOptions = quoteProductSubscriptionOptions.length === 0 && !isSubscriptionEnable;
    const isOneTimeEnableWithoutOptions = quoteProductSubscriptionOptions.length === 1 &&
                                          isOneTimeEnabled &&
                                          !isSubscriptionEnable;

    if (isOneTimeEnableWithoutOptions || isNoOptions) {
      setOpenConfirmDialog(true);
    } else {
      const productToUpdate = {
        sku: editableQuoteProduct?.sku,
        custom_attributes: [
          {attribute_code: 'subscription_active', value: checked ? 1 : 0},
          {attribute_code: 'catalog_quotepro_groups', value: currentGroupsValue}
        ]
      };
      const task = {
        body: productToUpdate,
        name: editableQuoteProduct?.name,
        sku: editableQuoteProduct?.sku,
        requestType: 'UPDATE_PRODUCT',
        storeViewCode: quoteProStoreCode
      };
      addTasksToQueueApi({dispatch, tasks: [task]});
    }
  };

  return (
    <Grid container spacing={3} alignItems="center">
      <Grid item xs={3}>Enable:</Grid>
      <Grid item xs={9}>
        {isLoading
          ? <CircularProgress className="ml-2" />
          :
          <Switch
            color="primary"
            name="subscription_active"
            disabled={isLoading}
            checked={isSubscriptionActive}
            onClick={(event) => {
              event.stopPropagation();
              const {checked} = event.target as HTMLInputElement;
              handleChangeSubscriptionStatus(checked);
            }}
          />
        }
      </Grid>
      <Grid item xs={3}>Allow One Time Purchase:</Grid>
      <Grid item xs={9}>
        {isLoading
          ? <CircularProgress className="ml-2" />
          :
          <Switch
            color="primary"
            name="subscription_allow_onetime"
            disabled={isLoading}
            checked={isOneTimeEnable}
            onChange={({target: {checked}}) => checked
              ? createOneTimePurchaseOption()
              : deleteOneTimePurchaseOption()
            }
          />
        }
      </Grid>
      <SubscriptionAttributeInput
        label="Subscription Label:"
        attributeCode="subscription_label"
        savedValue={subscriptionLabel}
        isLoading={isLoading}
      />
      {isMembershipProduct &&
      <>
        <SubscriptionAttributeInput
          label="Minimum Time Period (days):"
          attributeCode="minimum_time_period"
          savedValue={minimumTimePeriod}
          isLoading={isLoading}
        />
        <SubscriptionAttributeSelect
          label="Rebill Date:"
          attributeCode="rebill_date"
          savedValue={rebillDate}
          selectOptionsData={quoteProProductAttributeByCode?.['rebill_date'] || []}
          isLoading={isLoading}
        />
        <SubscriptionAttributeSelect
          label="On subscription, move to group:"
          attributeCode="subscription_include_to_group"
          savedValue={customerIncludeToGroup}
          selectOptionsData={customerGroups}
          isLoading={isLoading}
        />
        <SubscriptionAttributeSelect
          label="On unsubscription/expiry, move to group:"
          attributeCode="subscription_exclude_to_group"
          savedValue={customerExcludeToGroup}
          selectOptionsData={customerGroups}
          isLoading={isLoading}
        />
      </>
      }
      <Grid item xs={12}>Subscription Options:</Grid>
      {isLoading
        ? <CircularProgress className="ml-2" />
        :
        <SubscriptionOptionsTable
          setEditableSubscription={setEditableSubscription}
        />
      }
      <Grid item xs={12}>
        {!isLoading &&
          <AddSubscriptionButton
            isAllowToAddSubscription={isAllowToAddSubscription}
            setOpenAddingSubscriptionsDialog={setOpenAddingSubscriptionsDialog}
          />
        }
        <AddSubscriptionModal
          existFrequency={existFrequency}
          isOneTimeEnable={isOneTimeEnable}
          openAddingSubscriptionsDialog={openAddingSubscriptionsDialog}
          setOpenAddingSubscriptionsDialog={setOpenAddingSubscriptionsDialog}
          handleSaveSubscriptionOption={handleSaveSubscriptionOption}
        />
        <EditSubscriptionModal
          existFrequency={existFrequency}
          editableSubscription={editableSubscription}
          setEditableSubscription={setEditableSubscription}
          handleSaveSubscriptionOption={handleSaveSubscriptionOption}
        />
        <ConfirmDialog
          title="Do you want to enable Subscription?"
          open={openConfirmDialog}
          setOpen={setOpenConfirmDialog}
          onConfirm={() => {
            setOpenConfirmDialog(false);
            setOpenAddingSubscriptionsDialog(true);
          }}
        >
          Firstly add a subscription options to enable subscription for this product
        </ConfirmDialog>
      </Grid>
    </Grid>
  );
};


export default Subscription;
